import diff from 'difflib';
import { PronunciationAssessmentResult, SpeechRecognitionResult } from 'microsoft-cognitiveservices-speech-sdk';
import { isPunctuation, splitSentenceToWords } from '@doltech/utils/lib/sentence';
import { expandAbbreviationToFullWord } from '@doltech/utils/lib/abbreviation';
import { AssessmentConfigModel, ErrorTypeEnum, SpeechCorrectionModel } from './assessment-type';

const TEN_NANOSECONDS = 10000000;

interface WordResult {
  Word: string;
  Offset: number;
  Duration: number;
  PronunciationAssessment?: {
    AccuracyScore: number;
    ErrorType: string;
  };
  Phonemes: {
    Phoneme?: string;
    Offset: number;
    Duration: number;
    PronunciationAssessment?: {
      AccuracyScore: number;
      NBestPhonemes: {
        Phoneme: string;
        Score: number;
      }[];
    };
  }[];
  Syllables: {
    Syllable: string;
    Offset: number;
    Duration: number;
    PronunciationAssessment?: {
      AccuracyScore: number;
    };
  }[];
  IsPunctuation?: boolean;
}

export interface AssessmentResultModel {
  accuracyScore?: number;
  words?: SpeechCorrectionModel[];
}

export const mapSpeechResultToTranscript = (speechResults: SpeechRecognitionResult[], referenceText: string) => {
  const referenceWords = splitSentenceToWords(referenceText);
  return speechResults
    ?.flatMap((eachResult) => {
      const resultJson = JSON.parse(eachResult.json);
      return resultJson?.NBest?.[0]?.Words as WordResult[] || [];
    })
    .filter((eachWord) => eachWord?.PronunciationAssessment.AccuracyScore > 30)
    .map((eachWord, wordIndex) => {
      // A few adjustment to get correct word from the reference word
      const currentReferenceWord = referenceWords?.find((eachReferenceWord) => {
        const compareWord = eachWord.Word.toLowerCase();
        const compareReferenceWord = eachReferenceWord.toLowerCase();
        return (compareWord?.includes(compareReferenceWord) || compareReferenceWord.includes(compareWord))
          && Math.abs(compareReferenceWord?.length - compareWord?.length) < 3;
      });
      return currentReferenceWord || eachWord.Word;
    })
    .join(' ');
};

export const mapAssessmentResult = (
  assessmentResults: PronunciationAssessmentResult[],
  referenceText: string,
  config?: AssessmentConfigModel,
): AssessmentResultModel => {
  const allWords = assessmentResults?.flatMap((eachResult) => {
    return eachResult.detailResult.Words as WordResult[];
  });
  const resultWords: WordResult[] = [];

  const allTextWords = allWords.map((eachWord) => eachWord?.Word?.toLowerCase());
  const referenceWords = splitSentenceToWords(referenceText);
  const comparingReferenceWords = referenceWords.map((eachWord) => {
    return expandAbbreviationToFullWord(eachWord).toLowerCase();
  });
  const comparingAllTextWords = allTextWords.map((eachWord, wordIndex) => {
    // A few adjustment to get correct word from the reference word
    const currentReferenceWord = comparingReferenceWords?.find((eachReferenceWord) => {
      return (eachWord?.includes(eachReferenceWord) || eachReferenceWord.includes(eachWord))
        && Math.abs(eachReferenceWord?.length - eachWord?.length) < 3;
    });
    return currentReferenceWord || eachWord;
  });

  if (config?.enableMiscue) {
    /*
   * allTextWords: input text -> text need modify to become base text
   * referenceWords: reference text -> base text to follow
   * */
    const matcher = new diff.SequenceMatcher<string[]>(null, comparingAllTextWords, comparingReferenceWords);
    const deltas = matcher.getOpcodes();

    deltas.forEach((eachDelta) => {
      /*
       * Condition 'equal'
       * - Meaning: word is correct -> keep original word
       * - Error type: None | Mispronunciation -> Enum: NONE | MISPRONUNCIATION
       * */
      if (eachDelta[0] === 'equal') {
        const originalWords = allWords
          .slice(eachDelta[1], eachDelta[2])
          .map((eachWordResult, wordIndex) => ({
            ...eachWordResult,
            Word: referenceWords.slice(eachDelta[3], eachDelta[4])?.[wordIndex],
          }));
        resultWords.push(...originalWords);
      }
      /*
       * Condition 'insert' or 'replace'
       * - Meaning: missing word, need insert
       * - Error type: Omission -> Enum: OMISSION
       * */
      if (eachDelta[0] === 'insert' || eachDelta[0] === 'replace') {
        const missingWords = referenceWords.slice(eachDelta[3], eachDelta[4]).map((eachWord) => {
          if (isPunctuation(eachWord)) {
            return {
              Word: eachWord,
              IsPunctuation: true,
              PronunciationAssessment: {
                AccuracyScore: 100,
                ErrorType: ErrorTypeEnum.NONE,
              },
            } as WordResult;
          }

          return {
            Word: eachWord,
            PronunciationAssessment: {
              AccuracyScore: 0,
              ErrorType: ErrorTypeEnum.OMISSION,
            },
          } as WordResult;
        });
        resultWords.push(...missingWords);
      }

      /*
       * Condition 'delete' or 'replace'
       * - Meaning: word not available in reference text, need remove
       * - Error type: Insertion -> Enum: INSERTION
       * */
      if (eachDelta[0] === 'delete' || eachDelta[0] === 'replace') {
        const wrongInsertWords = allWords.slice(eachDelta[1], eachDelta[2]).map(
          (eachWord) =>
            ({
              ...eachWord,
              PronunciationAssessment: {
                ...(eachWord?.PronunciationAssessment || {}),
                ErrorType: ErrorTypeEnum.INSERTION,
              },
            } as WordResult)
        );
        resultWords.push(...wrongInsertWords);
      }
    });
  } else {
    const originalWords = referenceWords
      .map((eachReferenceWord) => {
        const foundWord = allWords.find((eachWord) => eachWord.Word.toLowerCase() === eachReferenceWord.toLowerCase());

        if (!foundWord) {
          return {
            Word: eachReferenceWord,
            PronunciationAssessment: {
              AccuracyScore: 0,
              ErrorType: ErrorTypeEnum.OMISSION,
            },
          }  as WordResult;
        }

        return foundWord;
      });
    resultWords.push(...originalWords);
  }

  const filteredResultWords = resultWords.filter(
    (eachWord) => ErrorTypeEnum.INSERTION !== eachWord.PronunciationAssessment.ErrorType);

  const accuracyScoreAverage = Math.round(
    filteredResultWords
      ?.filter((eachWord) => !eachWord.IsPunctuation)
      .reduce((sum, eachResult) => {
        return sum + eachResult.PronunciationAssessment.AccuracyScore;
      }, 0) / resultWords?.length
  );

  return {
    accuracyScore: accuracyScoreAverage,
    words: filteredResultWords?.map((eachWord, wordIndex) => {
      if (eachWord.IsPunctuation) {
        return {
          word: referenceWords[wordIndex],
          accuracyScore: eachWord.PronunciationAssessment.AccuracyScore,
          errorType: eachWord.PronunciationAssessment.ErrorType,
          isPunctuation: true,
        };
      }

      return {
        word: referenceWords[wordIndex],
        accuracyScore: eachWord.PronunciationAssessment.AccuracyScore,
        errorType: eachWord.PronunciationAssessment.ErrorType,
        rangeTimeInSeconds: eachWord.Offset && [
          eachWord.Offset / TEN_NANOSECONDS,
          (eachWord.Offset + eachWord.Duration) / TEN_NANOSECONDS,
        ],
        syllables: eachWord.Syllables?.map((eachSyllable) => ({
          syllable: eachSyllable.Syllable,
          accuracyScore: eachSyllable.PronunciationAssessment.AccuracyScore,
          rangeTimeInSeconds: [
            eachSyllable.Offset / TEN_NANOSECONDS,
            (eachSyllable.Offset + eachSyllable.Duration) / TEN_NANOSECONDS,
          ],
        })),
        phonemes: eachWord.Phonemes?.map((eachPhoneme) => ({
          phoneme: eachPhoneme.PronunciationAssessment.NBestPhonemes[0].Phoneme,
          bestPhoneme: eachPhoneme.Phoneme,
          accuracyScore: eachPhoneme.PronunciationAssessment.AccuracyScore,
          rangeTimeInSeconds: [
            eachPhoneme.Offset / TEN_NANOSECONDS,
            (eachPhoneme.Offset + eachPhoneme.Duration) / TEN_NANOSECONDS,
          ],
        })),
      };
    }),
  };
};
